import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    CircularProgress,
    Tabs,
    Tab,
    Button,
    MenuItem,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { axiosInstance } from '../../api';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, AVATAR_SX } from '../common/tableStyles';
import MonthlyAttendanceGrid from './MonthlyAttendanceGrid';
import CycleAttendanceDashboard from './CycleAttendanceDashboard';
import AttendanceUpload from './AttendanceUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WebAttendanceMonitor from './WebAttendance/WebAttendanceMonitor';
import BiometricMain from './Biometric/BiometricMain';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useDownloadAttendanceExport } from '../../hooks/useAttendance';
import PageHeader from '../employeeUI/PageHeader';

// ---- FORTHEYE soft-card tokens (shared with the Leave dashboards) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const GRADIENT_BTN = {
    textTransform: 'none', fontWeight: 700, borderRadius: '12px',
    background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
    '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const AttendancePresent = ({ teamOnly = false }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Get filter from query param if exists (e.g., ?filter=absent)
    const queryParams = new URLSearchParams(location.search);
    const initialFilter = queryParams.get('filter') || 'all';

    const [activeTab, setActiveTab] = useState(0);
    // location.key changes on every navigate() call, even ones that land on
    // this same pathname (e.g. clicking "Daily Status" in the sidebar or the
    // header search while already on this page, just on a different tab) —
    // reset to the Daily Status tab whenever that happens. Manual tab clicks
    // only call setActiveTab directly, never navigate(), so this doesn't
    // interfere with normal tab switching.
    useEffect(() => {
        setActiveTab(0);
    }, [location.key]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedBranch, setSelectedBranch] = useState('all');
    const [selectedManager, setSelectedManager] = useState('all');
    const [filterType, setFilterType] = useState(initialFilter);

    // One-click download of the selected date's attendance, scoped to the chosen
    // branch + reporting manager (matching the records shown on this page).
    const { mutate: downloadAttendance, isPending: isDownloading } = useDownloadAttendanceExport();
    const handleDownload = () => {
        downloadAttendance({ startDate: selectedDate, endDate: selectedDate, branch: selectedBranch, manager: selectedManager });
    };
    const [attendanceData, setAttendanceData] = useState({
        present: [],
        absent: [],
        branches: [],
        managers: [],
        total_strength: 0,
        present_count: 0,
        absent_count: 0,
        on_duty_count: 0,
        comp_off_count: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAttendance = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ date: selectedDate });
                if (teamOnly) params.append('team_scope', '1');
                if (selectedBranch && selectedBranch !== 'all') params.append('branch', selectedBranch);
                if (selectedManager && selectedManager !== 'all') params.append('manager', selectedManager);
                const response = await axiosInstance.get(`/api/attendance/daily-status/?${params.toString()}`);
                setAttendanceData(response.data);
            } catch (error) {
                console.error("Error fetching attendance data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAttendance();
    }, [selectedDate, selectedBranch, selectedManager, teamOnly]);

    const StatCard = ({ title, value, icon, color, bgColor, id }) => {
        const isSelected = filterType === id;
        return (
            <Paper
                elevation={0}
                onClick={() => setFilterType(id)}
                sx={{
                    p: 2,
                    borderRadius: '16px',
                    bgcolor: isSelected ? `${color}0d` : '#fff',
                    border: isSelected ? `1px solid ${color}33` : '1px solid #eef0f4',
                    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 28px rgba(17,24,39,0.08)'
                    },
                }}
            >
                <Box sx={{
                    width: 42,
                    height: 42,
                    bgcolor: `${color}14`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: color,
                    flexShrink: 0,
                }}>
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.62rem', display: 'block' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: color, lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    const EmployeeTable = ({ title, data, type }) => (
        <Box sx={{ mt: 3, animation: 'fadeIn 0.5s ease-out' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#111827', letterSpacing: '-0.02em' }}>
                    {title} <Chip label={data.length} size="small" sx={{ ml: 1, fontWeight: 700, border: 'none', bgcolor: type === 'present' ? '#f0fdf4' : '#fef2f2', color: type === 'present' ? '#16a34a' : '#ef4444' }} />
                </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Designation</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Department</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Reporting Manager</TableCell>
                            {type === 'absent' && (
                                <>
                                    <TableCell sx={HEAD_CELL_SX}>Requested Date</TableCell>
                                    <TableCell sx={HEAD_CELL_SX}>Approval Date</TableCell>
                                </>
                            )}
                            <TableCell sx={HEAD_CELL_SX}>Contact</TableCell>
                            {type === 'absent' && <TableCell align="center" sx={HEAD_CELL_SX}>Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={type === 'absent' ? 8 : 5} align="center" sx={{ py: 8 }}>
                                    <CircularProgress size={40} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading records...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : data.length > 0 ? data.map((emp) => (
                            <TableRow
                                key={emp.employee_no}
                                hover
                                sx={{ ...TABLE_ROW_SX, cursor: type === 'absent' ? 'pointer' : 'default' }}
                                onClick={() => type === 'absent' && navigate('/leave-tracker')}
                            >
                                <TableCell sx={BODY_CELL_SX}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar sx={AVATAR_SX}>
                                            {emp.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight={700} color="#111827">
                                                {emp.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {emp.employee_no}
                                            </Typography>
                                            {emp.is_half_day && (
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={`½ Day Leave${emp.leave_type && emp.leave_type !== 'Leave' ? ` · ${emp.leave_type}` : ''}`}
                                                        size="small"
                                                        sx={{ height: 18, fontSize: 10, fontWeight: 700, border: 'none', bgcolor: '#FFF3E0', color: '#B45309' }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={BODY_CELL_SX}><Typography variant="body2">{emp.designation || 'N/A'}</Typography></TableCell>
                                <TableCell sx={BODY_CELL_SX}><Typography variant="body2">{emp.department || 'N/A'}</Typography></TableCell>
                                <TableCell sx={BODY_CELL_SX}><Typography variant="body2" fontWeight={500}>{emp.reporting_manager || 'N/A'}</Typography></TableCell>
                                {type === 'absent' && (
                                    <>
                                        <TableCell sx={BODY_CELL_SX}>
                                            <Chip label={emp.requested_date || 'N/A'} size="small" sx={{ fontWeight: 700, border: 'none', bgcolor: '#eef4ff', color: ACCENT }} />
                                        </TableCell>
                                        <TableCell sx={BODY_CELL_SX}>
                                            <Chip label={emp.approved_date || 'N/A'} size="small" sx={{ fontWeight: 700, border: 'none', bgcolor: '#f0fdf4', color: '#16a34a' }} />
                                        </TableCell>
                                    </>
                                )}
                                <TableCell sx={BODY_CELL_SX}><Typography variant="body2">{emp.mobile_number || 'N/A'}</Typography></TableCell>
                                {type === 'absent' && (
                                    <TableCell align="center" sx={BODY_CELL_SX}>
                                        <Tooltip title="View in Tracker">
                                            <IconButton size="small" sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}>
                                                <ArrowForwardIosIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={type === 'absent' ? 8 : 5} align="center" sx={{ py: 8, border: 'none' }}>
                                    <Stack alignItems="center" spacing={1.5}>
                                        <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {type === 'present' ? <PersonIcon /> : <PersonOffIcon />}
                                        </Box>
                                        <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No records found for this date.</Typography>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const getFilteredPresent = () => {
        if (filterType === 'on_duty') {
            return attendanceData.present.filter(e => e.is_on_duty);
        }
        return attendanceData.present;
    };

    const getFilteredAbsent = () => {
        if (filterType === 'comp_off') {
            return attendanceData.absent.filter(e => e.leave_type === 'Comp Off');
        }
        return attendanceData.absent;
    }

    return (
        <Box sx={{
            p: teamOnly ? 0 : { xs: 2, md: 4 },
            '& @keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
            }
        }}>
            {/* Top tabs */}
            <Paper
                elevation={0}
                sx={{
                    ...CARD_SX,
                    p: 0,
                    mb: { xs: 2, md: 3 },
                    overflow: 'hidden',
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 15,
                            minHeight: 56,
                        },
                        '& .Mui-selected': { color: `${ACCENT} !important` },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3 },
                    }}
                >
                    <Tab icon={<TodayIcon />} iconPosition="start" label="Daily Status" />
                    <Tab icon={<DateRangeIcon />} iconPosition="start" label="Monthly Overview" />
                    <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Cycle (26–25)" />
                    {!teamOnly && <Tab icon={<UploadFileIcon />} iconPosition="start" label="Upload Attendance" />}
                    {!teamOnly && <Tab icon={<VisibilityIcon />} iconPosition="start" label="Web Attendance" />}
                    {!teamOnly && <Tab icon={<FingerprintIcon />} iconPosition="start" label="Biometric Devices" />}
                </Tabs>
            </Paper>

            {activeTab === 1 && <MonthlyAttendanceGrid teamOnly={teamOnly} />}
            {activeTab === 2 && <CycleAttendanceDashboard teamOnly={teamOnly} />}
            {!teamOnly && activeTab === 3 && <AttendanceUpload />}
            {!teamOnly && activeTab === 4 && <WebAttendanceMonitor />}
            {!teamOnly && activeTab === 5 && <BiometricMain />}

            {activeTab === 0 && (<>
            {/* Header card */}
            {teamOnly ? (
                <PageHeader
                    icon={<TodayIcon />}
                    title="Team Daily Attendance Status"
                    subtitle="Daily attendance status for employees directly assigned to you."
                />
            ) : <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TodayIcon />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                            {teamOnly ? 'Team Daily Attendance Status' : 'Daily Attendance Status'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {teamOnly ? 'Daily attendance status for employees directly assigned to you.' : 'Real-time presence monitoring based on approved leave data.'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>}

            {/* Filters card */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Paper elevation={0} sx={{ p: 1, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ color: '#6b7280', ml: 1 }} />
                    <TextField
                        select
                        size="small"
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiInputBase-input': { fontWeight: 600, color: '#111827' }
                        }}
                    >
                        <MenuItem value="all">All Branches</MenuItem>
                        {(attendanceData.branches || []).map((b) => (
                            <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                    </TextField>
                </Paper>
                {!teamOnly && <Paper elevation={0} sx={{ p: 1, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SupervisorAccountIcon sx={{ color: '#6b7280', ml: 1 }} />
                    <TextField
                        select
                        size="small"
                        value={selectedManager}
                        onChange={(e) => setSelectedManager(e.target.value)}
                        sx={{
                            minWidth: 160,
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiInputBase-input': { fontWeight: 600, color: '#111827' }
                        }}
                    >
                        <MenuItem value="all">All Managers</MenuItem>
                        {(attendanceData.managers || []).map((m) => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                    </TextField>
                </Paper>}
                <Paper elevation={0} sx={{ p: 1, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #eef0f4', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarMonthIcon sx={{ color: '#6b7280', ml: 1 }} />
                    <TextField
                        type="date"
                        size="small"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiInputBase-input': { fontWeight: 600, color: '#111827' }
                        }}
                    />
                </Paper>
                <Box sx={{ flexGrow: 1 }} />
                {!teamOnly && <Button
                    variant="contained"
                    disableElevation
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={isDownloading}
                    sx={{ ...GRADIENT_BTN, py: 1.1, px: 2.5 }}
                >
                    {isDownloading ? 'Preparing…' : 'Download'}
                </Button>}
            </Paper>

            {/* Stats Grid */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                        id="all"
                        title="Total Employees"
                        value={attendanceData.total_strength}
                        icon={<GroupIcon />}
                        color="#6366F1"
                        bgColor="#F5F3FF"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                        id="present"
                        title="Present"
                        value={attendanceData.present_count}
                        icon={<PersonIcon />}
                        color="#10B981"
                        bgColor="#F0FDF4"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                        id="absent"
                        title="Absent"
                        value={attendanceData.absent_count}
                        icon={<PersonOffIcon />}
                        color="#EF4444"
                        bgColor="#FEF2F2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                        id="on_duty"
                        title="On Duty"
                        value={attendanceData.on_duty_count}
                        icon={<BadgeIcon />}
                        color="#F59E0B"
                        bgColor="#FFFBEB"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <StatCard
                        id="comp_off"
                        title="Comp Off"
                        value={attendanceData.comp_off_count}
                        icon={<TimerOffIcon />}
                        color="#6B7280"
                        bgColor="#F9FAFB"
                    />
                </Grid>
            </Grid>

            {/* Tables Section */}
            {(filterType === 'all' || filterType === 'present' || filterType === 'on_duty') && (
                <EmployeeTable
                    title={filterType === 'on_duty' ? "On Duty Employees" : "Present Employees"}
                    data={getFilteredPresent()}
                    type="present"
                />
            )}

            {(filterType === 'all' || filterType === 'absent' || filterType === 'comp_off') && (
                <EmployeeTable
                    title={filterType === 'comp_off' ? "Comp Off Employees" : "Absent Employees"}
                    data={getFilteredAbsent()}
                    type="absent"
                />
            )}
            </>)}
        </Box>
    );
};

export default AttendancePresent;
